function formatCompilerError(errLog: string, src: string, type?: number) {
  let typeName = 'unknown type'
  if (type !== undefined) {
    typeName =
      type === WebGLRenderingContext.FRAGMENT_SHADER ? 'fragment' : 'vertex'
  }

  let longForm = `Error compiling ${typeName} shader:\n`
  const shortForm = longForm + errLog

  const errorStrings = errLog.split('\n')
  const errors: Record<number, string[]> = {}

  for (const errorString of errorStrings) {
    if (errorString === '' || errorString === '\0') continue
    var lineNo = parseInt(errorString.split(':')[2])
    if (isNaN(lineNo)) {
      throw new Error(`Could not parse error: ${errorString}`)
    }
    errors[lineNo] = errors[lineNo] || []
    errors[lineNo].push(errorString)
  }

  const lines = src
    .split('\n')
    .map((line, index) => `${index.toString().padStart(3)}: ${line}`)

  for (var i = 0; i < lines.length; i++) {
    if (!errors[i + 3] && !errors[i + 2] && !errors[i + 1]) continue
    const line = lines[i]
    longForm += line + '\n'
    if (errors[i + 1]) {
      for (const e of errors[i + 1]) {
        const fmt = e.substring(e.split(':', 3).join(':').length + 1).trim()
        longForm += `^^^ ${fmt}\n`
      }
      longForm += '\n'
    }
  }

  return {
    long: longForm.trim(),
    short: shortForm.trim()
  }
}

type ProgramSource<U extends string> = {
  vs: string
  fs: string
  attributes: readonly (readonly [name: string, size: number, type: number])[]
  uniforms: readonly U[]
}

type ProgramInfo<U extends string> = {
  program: WebGLProgram
  attributes: Parameters<WebGLRenderingContext['vertexAttribPointer']>[]
  uniforms: Record<U, WebGLUniformLocation>
  vertexSize: number
}

export type ProgramType<Source> = Source extends ProgramSource<infer U>
  ? ProgramInfo<U>
  : never

function loadShader(gl: WebGLRenderingContext, type: number, source: string) {
  const shader = gl.createShader(type)!
  gl.shaderSource(shader, source)
  gl.compileShader(shader)
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(shader)
    const fmt = formatCompilerError(log ?? '', source, type)
    console.warn(fmt.long)
    throw Error(fmt.short)
  }
  return shader
}

function attributeSize(type: number) {
  switch (type) {
    case WebGLRenderingContext.BYTE:
    case WebGLRenderingContext.UNSIGNED_BYTE:
      return 1
    case WebGLRenderingContext.SHORT:
    case WebGLRenderingContext.UNSIGNED_SHORT:
      return 2
    case WebGLRenderingContext.FLOAT:
      return 4
    default:
      throw Error(`invalid attribute type ${type}`)
  }
}

export function createProgram<U extends string>(
  gl: WebGLRenderingContext,
  source: ProgramSource<U>
) {
  const vs = loadShader(gl, WebGLRenderingContext.VERTEX_SHADER, source.vs)
  const fs = loadShader(
    gl,
    WebGLRenderingContext.FRAGMENT_SHADER,
    'precision mediump float;\n' + source.fs
  )

  const program = gl.createProgram()!
  gl.attachShader(program, vs)
  gl.attachShader(program, fs)
  gl.linkProgram(program)
  if (!gl.getProgramParameter(program, WebGLRenderingContext.LINK_STATUS)) {
    throw Error(`failed to link shader: ${gl.getProgramInfoLog(program)}`)
  }

  const attributes: ProgramInfo<U>['attributes'] = []
  let stride = 0
  for (const [name, size, type] of source.attributes) {
    stride += attributeSize(type) * size
  }
  let offset = 0
  for (const [name, size, type] of source.attributes) {
    const loc = gl.getAttribLocation(program, name)
    if (loc >= 0) {
      attributes.push([
        loc,
        size,
        type,
        type !== WebGLRenderingContext.FLOAT,
        stride,
        offset
      ])
    }
    offset += attributeSize(type) * size
  }

  const uniforms: { [key in U]?: WebGLUniformLocation } = {}
  for (const name of source.uniforms) {
    const loc = gl.getUniformLocation(program, name)!
    //if (loc == null) throw Error(`unknown uniform ${name}`)
    uniforms[name] = loc
  }

  return {
    program,
    attributes,
    uniforms,
    vertexSize: stride
  } as ProgramInfo<U>
}

export function bindVertices(
  gl: WebGLRenderingContext,
  buffer: WebGLBuffer,
  attributes: Parameters<WebGLRenderingContext['vertexAttribPointer']>[]
) {
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer)
  for (const attr of attributes) {
    gl.vertexAttribPointer(...attr)
    gl.enableVertexAttribArray(attr[0])
  }
}
